import type { ShipmentMerchandise } from "./types";

export const MERCHANDISE_UNIT_TYPES = [
  "PZA",
  "KG",
  "TON",
  "LT",
  "M3",
  "BOX",
  "PLT",
] as const;

export interface MerchandiseDraft {
  uid: string;
  id?: string;
  description: string;
  quantity: string;
  unitType: string;
  weight: string;
  volume: string;
}

export type MerchandiseFieldError =
  | "descriptionRequired"
  | "quantityInvalid"
  | "weightInvalid"
  | "volumeInvalid";

export type MerchandiseDraftErrors = Partial<
  Record<"description" | "quantity" | "weight" | "volume", MerchandiseFieldError>
>;

let draftCounter = 0;

const nextUid = (): string => {
  draftCounter += 1;
  return `merchandise-${draftCounter}`;
};

export const createMerchandiseDraft = (): MerchandiseDraft => ({
  uid: nextUid(),
  description: "",
  quantity: "1",
  unitType: "PZA",
  weight: "",
  volume: "",
});

export const merchandiseToDrafts = (
  items: readonly ShipmentMerchandise[],
): MerchandiseDraft[] =>
  items.map((item) => ({
    uid: nextUid(),
    ...(item.id && { id: item.id }),
    description: item.description,
    quantity: String(item.quantity),
    unitType: item.unitType || "PZA",
    weight: item.weight === undefined ? "" : String(item.weight),
    volume: item.volume === undefined ? "" : String(item.volume),
  }));

const isBlank = (value: string): boolean => value.trim() === "";

const isUntouched = (draft: MerchandiseDraft): boolean =>
  isBlank(draft.description) && isBlank(draft.weight) && isBlank(draft.volume);

export const isValidQuantity = (value: string): boolean => {
  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) && Number(trimmed) >= 1;
};

export const isValidNonNegative = (value: string): boolean => {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0;
};

export const validateMerchandiseDraft = (
  draft: MerchandiseDraft,
): MerchandiseDraftErrors => ({
  ...(isBlank(draft.description) && { description: "descriptionRequired" as const }),
  ...(!isValidQuantity(draft.quantity) && { quantity: "quantityInvalid" as const }),
  ...(!isValidNonNegative(draft.weight) && { weight: "weightInvalid" as const }),
  ...(!isValidNonNegative(draft.volume) && { volume: "volumeInvalid" as const }),
});

export const activeMerchandiseDrafts = (
  drafts: readonly MerchandiseDraft[],
): MerchandiseDraft[] => drafts.filter((draft) => !isUntouched(draft));

export const validateMerchandiseDrafts = (
  drafts: readonly MerchandiseDraft[],
): Record<string, MerchandiseDraftErrors> =>
  Object.fromEntries(
    activeMerchandiseDrafts(drafts)
      .map((draft) => [draft.uid, validateMerchandiseDraft(draft)] as const)
      .filter(([, errors]) => Object.keys(errors).length > 0),
  );

export const draftsToMerchandise = (
  drafts: readonly MerchandiseDraft[],
): ShipmentMerchandise[] =>
  activeMerchandiseDrafts(drafts).map((draft) => ({
    ...(draft.id && { id: draft.id }),
    description: draft.description.trim(),
    quantity: Number(draft.quantity.trim()),
    unitType: draft.unitType || "PZA",
    ...(!isBlank(draft.weight) && { weight: Number(draft.weight.trim()) }),
    ...(!isBlank(draft.volume) && { volume: Number(draft.volume.trim()) }),
  }));
